import { GlassType } from '../types/glassTypes';
import { convertThicknessToNumber, parseCSVString, parseCSVToGlassTypes } from '../utils/csvUtils';
import { 
  loadAllGlassTypes, 
  loadGlassTypesFromWholeFile, 
  getFallbackGlassTypes
} from '../utils/glassTypeLoader';

// Export fallback glass types for use in other parts of the application
export const fallbackClgGlassTypes: GlassType[] = getFallbackGlassTypes('CLG');
export const fallbackOldCastleGlassTypes: GlassType[] = getFallbackGlassTypes('OldCastle');

/**
 * Validates and normalizes glass type data
 * @param types Array of glass types to validate
 * @returns Validated and normalized glass types
 */
const validateGlassTypes = (types: GlassType[]): GlassType[] => {
  if (types.length === 0) return types;
  
  console.log(`Validating ${types.length} glass types`);
  if (types.length > 0) {
    console.log('Sample glass type before validation:', types[0]);
  }

  // Ensure all types have the correct structure
  const validatedTypes = types.map(type => {
    // Make sure type field is properly normalized
    const normalizedType = type.type.charAt(0).toUpperCase() + type.type.slice(1).toLowerCase();

    return {
      ...type,
      type: normalizedType,
      // Ensure numeric fields are numbers
      thickness: typeof type.thickness === 'number' ? type.thickness : convertThicknessToNumber(type.thicknessStr || ''),
      lbsPerSqFt: typeof type.lbsPerSqFt === 'number' ? type.lbsPerSqFt : 0,
      price: typeof type.price === 'number' ? type.price : 0
    };
  });

  if (validatedTypes.length > 0) {
    console.log('Sample glass type after validation:', validatedTypes[0]);
  }

  return validatedTypes;
};

/**
 * Gets glass types for CLG from the glass_pricing folder
 * @returns Promise resolving to an array of GlassType objects
 */
export const getClgGlassTypes = async (): Promise<GlassType[]> => {
  try {
    console.log('Loading CLG glass types from glass_pricing folder');
    
    let types: GlassType[] = [];
    
    // Try to load from the whole file first
    console.log('Attempting to load from whole file');
    types = await loadGlassTypesFromWholeFile('CLG');
    
    // If that fails, try loading from individual category files
    if (types.length === 0) {
      console.log('Whole file loading failed, trying individual category files');
      types = await loadAllGlassTypes('CLG');
    }
    
    console.log(`Loaded ${types.length} CLG glass types`);
    
    if (types.length === 0) {
      console.log('No CLG glass types found, using fallback data');
      return fallbackClgGlassTypes;
    }
    
    return validateGlassTypes(types);
  } catch (error) {
    console.error('Error loading CLG glass types:', error);
    console.log('Using fallback CLG glass types');
    return fallbackClgGlassTypes;
  }
};

/**
 * Gets glass types for OldCastle from the glass_pricing folder
 * @returns Promise resolving to an array of GlassType objects
 */
export const getOldCastleGlassTypes = async (): Promise<GlassType[]> => {
  try {
    console.log('Loading OldCastle glass types from glass_pricing folder');
    
    let types: GlassType[] = [];
    
    // Try to load from the whole file first
    console.log('Attempting to load from whole file');
    types = await loadGlassTypesFromWholeFile('OldCastle');
    
    // If that fails, try loading from individual category files
    if (types.length === 0) {
      console.log('Whole file loading failed, trying individual category files');
      types = await loadAllGlassTypes('OldCastle');
    }
    
    console.log(`Loaded ${types.length} OldCastle glass types`);
    
    if (types.length === 0) {
      console.log('No OldCastle glass types found, using fallback data');
      return fallbackOldCastleGlassTypes;
    }
    
    return validateGlassTypes(types);
  } catch (error) {
    console.error('Error loading OldCastle glass types:', error);
    console.log('Using fallback OldCastle glass types');
    return fallbackOldCastleGlassTypes;
  }
};

/**
 * Processes an uploaded CSV file and converts it to GlassType objects
 * @param file The uploaded CSV file
 * @param sourceName The name to use as the source identifier
 * @returns Promise resolving to an array of GlassType objects
 */
export const processUploadedCSV = async (file: File, sourceName: string): Promise<GlassType[]> => {
  try {
    console.log(`Processing uploaded CSV file: ${file.name} for source: ${sourceName}`);
    
    // Read the file content
    const csvData = await file.text();
    console.log(`Successfully read CSV data, first 100 chars:`, csvData.substring(0, 100));
    
    // Parse the CSV data
    const result = parseCSVToGlassTypes(csvData, sourceName);
    console.log(`Parsed ${result.length} glass types from uploaded file`);
    
    return validateGlassTypes(result);
  } catch (error) {
    console.error('Error processing uploaded CSV file:', error);
    return [];
  }
};
